// pages/superadmin/PipelineBoard.jsx
//
// Phase 5C, Pipeline Management. Kanban-style board over
// pipelineService.js's 6-stage funnel (new -> contacted -> qualified ->
// proposal -> won/lost). Calls GET /api/pipeline/board and
// POST /api/pipeline/:id/stage.
//
// Styling mirrors SalesWorkspace.jsx / AiPerformanceDashboard.jsx (same
// tokens, same header pattern) rather than a new visual language.
// Rendered as the "Pipeline" tab inside CustomerDashboard for both the
// internal team and Phase 5B partners, server-side scoping (customerId)
// does the rest.
//
// Stage transitions are validated server-side (pipelineService.isValidTransition):
// one step forward/back, or straight to "lost" from any non-terminal stage.
// isValidTransition() below mirrors that rule for both the move buttons and
// drag-and-drop, so a disabled-looking button, a drag target that won't
// accept a drop, and a 400 from the API never disagree.
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import useRevenueApi from "../../../hooks/useRevenueApi";
import InfoTooltip from "../../../components/InfoTooltip";
import useSubnavSlot from "../../../components/useSubnavSlot";
import { createPortal } from "react-dom";
import { scopeCss } from "../../../utils/scopeCss";
import { Skeleton } from "../../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../../utils/themeTransition";
import { productLabel } from "../../../utils/labels";

const EMBED_SCOPE = ".ri-embed-pipeline";

const styles = `
  /* Cal.com-style system: white canvas, hairline cards, near-black ink,
   * sentence-case Inter labels, status shown as a dot not a tint. Mirrors
   * SalesWorkspace.jsx. */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }



  /* dark: flat cards need a hairline to separate from the #101010 canvas */
  .dash-root.dark .metric-card.is-hero { border-color: var(--border); }

  html, body { overflow-x: hidden; }
  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased;  }

  /* HEADER, flat AXO navy, same as SalesWorkspace */
  .dash-header { background: var(--header-bg); border-bottom: 1px solid var(--header-border); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1520px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; }
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); border-radius: var(--radius-sm); color: var(--header-text); text-decoration: none; }
  .btn-back:hover { background: var(--header-btn-hover-bg); color: #fff; }
  .dash-brand-mark { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; border-radius: var(--radius-sm); background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); color: #fff; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 14px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-size: 12px; color: var(--header-text-muted); }
  .dash-header-actions { display: flex; align-items: center; gap: 8px; padding: 12px 0; }
  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: var(--header-text); cursor: pointer; white-space: nowrap; text-decoration: none; transition: background var(--transition), color var(--transition), border-color var(--transition); }
  .btn-header:hover:not(:disabled) { background: var(--header-btn-hover-bg); color: #fff; }
  .btn-header:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-header:focus-visible { outline: 2px solid rgba(255,255,255,0.6); outline-offset: 2px; }
  .btn-header.is-accent { background: rgba(255,255,255,0.12); }
  .btn-header svg { flex-shrink: 0; }
  .btn-header.is-refreshing svg { animation: refresh-spin 0.6s linear; }
  @keyframes refresh-spin { to { transform: rotate(360deg); } }

  .dash-main { max-width: 1520px; margin: 0 auto; padding: 32px 28px 56px; display: flex; flex-direction: column; gap: 20px; min-width: 0; }
  .dash-main > div { animation: card-in 0.25s ease both; }
  @keyframes card-in { from { opacity: 0; } to { opacity: 1; } }

  /* SECTIONS, plain sentence-case heading, no rule, no mono */
  .section-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
  .section-title { display: inline-flex; align-items: center; gap: 4px; font-size: 16px; font-weight: 600; letter-spacing: -0.01em; color: var(--text); }
  .section-caption { font-size: 13px; color: var(--text-muted); }

  /* METRIC CARDS, white + hairline; grey fill marks the headline figure */
  .metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
  .metric-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px 22px; min-width: 0; }
  .metric-card.is-hero { background: var(--surface-sunken); border-color: transparent; }
  .metric-label { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 500; color: var(--text-muted); margin-bottom: 8px; }
  .metric-value { font-size: 28px; font-weight: 600; line-height: 1.15; color: var(--text); letter-spacing: -0.03em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .metric-value.muted { color: var(--text-faint); font-size: 15px; font-weight: 500; letter-spacing: 0; }
  .metric-note { font-size: 13px; color: var(--text-muted); margin-top: 6px; }

  /* BADGE, neutral pill, a small dot carries the status */
  .badge { display: inline-flex; align-items: center; gap: 6px; font-family: var(--sans); font-size: 12px; font-weight: 500; letter-spacing: 0; text-transform: none; padding: 3px 10px; border-radius: 9999px; background: var(--surface-sunken); border: none; color: var(--text); white-space: nowrap; }
  .badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .badge.confidence-moderate, .badge.direction-growing { --dot: var(--green); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.confidence-low { --dot: var(--amber); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.direction-declining { --dot: var(--red); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.confidence-insufficient_data, .badge.direction-flat { --dot: var(--text-faint); color: var(--text-muted); background: var(--surface-sunken); border: none; }

  /* CHART, one ink colour, no gradient */
  .chart-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 24px; }
  .chart-bars { display: flex; align-items: flex-end; gap: 12px; height: 150px; padding-top: 8px; }
  .chart-bar-col { flex: 1 1 0; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; min-width: 0; gap: 8px; }
  .chart-bar-track { width: 100%; flex: 1 1 auto; display: flex; align-items: flex-end; }
  .chart-bar-fill { width: 100%; border-radius: 4px 4px 0 0; background: var(--ink); min-height: 3px; transform-origin: bottom; animation: bar-grow-y 0.4s ease-out both; }
  @keyframes bar-grow-y { from { transform: scaleY(0); } to { transform: scaleY(1); } }
  .chart-bar-fill.is-empty { background: var(--border); }
  .chart-bar-value { font-size: 12px; font-weight: 500; color: var(--text-muted); white-space: nowrap; font-variant-numeric: tabular-nums; }
  .chart-bar-label { font-size: 12px; color: var(--text-muted); white-space: nowrap; }

  /* LIST ROWS */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .breakdown-row { display: flex; align-items: center; gap: 16px; padding: 16px 22px; border-bottom: 1px solid var(--border); }
  .breakdown-row:last-child { border-bottom: none; }
  .breakdown-main { flex: 1 1 auto; min-width: 0; }
  .breakdown-task { font-size: 14px; font-weight: 500; color: var(--text); }
  .breakdown-sub { font-size: 13px; color: var(--text-muted); margin-top: 1px; }
  .breakdown-bar-track { width: 100%; height: 4px; border-radius: 9999px; background: var(--surface-sunken); margin-top: 10px; overflow: hidden; }
  .breakdown-bar-fill { height: 100%; border-radius: 9999px; background: var(--ink); transform-origin: left; animation: bar-grow 0.4s ease-out both; }
  @keyframes bar-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
  .breakdown-cost { flex-shrink: 0; font-size: 14px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }

  .notice-card { display: flex; align-items: flex-start; gap: 10px; background: var(--surface-sunken); border: none; border-radius: var(--radius); padding: 12px 16px; font-size: 14px; line-height: 1.5; color: var(--text-body); }
  .notice-card svg { flex-shrink: 0; color: var(--amber); margin-top: 3px; }

  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px 20px; gap: 10px; text-align: center; }
  .empty-state-label { font-size: 14px; color: var(--text-muted); }
  .spinner { width: 24px; height: 24px; border: 2px solid var(--border); border-top-color: var(--ink); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* BOARD, grey columns hold white cards */
  .board-scroll { overflow-x: auto; padding-bottom: 8px; }
  .board { display: flex; gap: 12px; min-width: min-content; }
  .board-col { width: 264px; flex-shrink: 0; background: var(--surface-sunken); border-radius: var(--radius-lg); display: flex; flex-direction: column; max-height: calc(100vh - 150px); }
  .board-col-header { padding: 14px 16px 10px; display: flex; align-items: center; justify-content: space-between; }
  .board-col-title { display: inline-flex; align-items: center; gap: 4px; font-size: 14px; font-weight: 600; color: var(--text); }
  .board-col-title::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .board-col-title.won { color: var(--text); --dot: var(--green); }
  .board-col-title.lost { color: var(--text); --dot: var(--red); }
  .board-col-count { font-size: 13px; color: var(--text-muted); font-variant-numeric: tabular-nums; }
  .board-col-body { padding: 4px 10px 10px; display: flex; flex-direction: column; gap: 8px; overflow-y: auto; }

  .pipe-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 12px; }
  .pipe-card.draggable { cursor: grab; }
  .pipe-card.draggable:active { cursor: grabbing; }
  .pipe-card.dragging { opacity: 0.4; }
  .board-col-body.drag-over { background: var(--border); outline: 2px dashed var(--border-strong); outline-offset: -4px; border-radius: var(--radius); }
  .pipe-card-name { font-size: 14px; font-weight: 600; letter-spacing: -0.01em; color: var(--text); margin-bottom: 4px; }
  .pipe-card-sub { font-size: 13px; color: var(--text-muted); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 12px; }
  .pipe-card-product { font-size: 12px; font-weight: 500; text-transform: none; letter-spacing: 0; padding: 1px 8px; border-radius: 9999px; background: var(--surface-sunken); border: none; color: var(--text); }
  /* Two-up grid with "Lost" spanning the full width: three equal buttons in one
     264px row overflow their labels and read as cramped. */
  .pipe-card-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding-top: 12px; border-top: 1px solid var(--border); }

  .btn-mini { min-width: 0; display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 34px; border-radius: var(--radius-sm); font-family: var(--sans); font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid var(--border); background: var(--surface); color: var(--text); padding: 0 12px; white-space: nowrap; transition: background var(--transition), border-color var(--transition), color var(--transition); }
  .btn-mini:hover:not(:disabled) { background: var(--surface-sunken); border-color: var(--border-strong); }
  .btn-mini:active:not(:disabled) { background: var(--surface-sunken); }
  .btn-mini:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .btn-mini:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-mini.lost { grid-column: 1 / -1; color: var(--red); border-color: var(--border); }
  .btn-mini.lost:hover:not(:disabled) { background: var(--red-dim); border-color: var(--red-border); }

  .dash-root.dark .board-col { border: 1px solid var(--border); }
  .dash-root.dark .pipe-card { background: var(--surface-raised); }
  .dash-root.dark .btn-mini { background: transparent; }

  .empty-col { font-size: 13px; color: var(--text-faint); text-align: center; padding: 24px 8px; }
  .board-error { background: var(--surface-sunken); color: var(--text); border-radius: var(--radius); padding: 12px 16px; font-size: 14px; display: flex; align-items: center; gap: 10px; }
  .board-error::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--red); flex-shrink: 0; }
  .board-loading { font-size: 14px; color: var(--text-muted); padding: 40px; text-align: center; }

  .two-col { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 16px; }
  .board-card { padding: 16px; }
  .summary-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 22px; border-bottom: 1px solid var(--border); font-size: 13px; color: var(--text-muted); }
  .summary-row:last-child { border-bottom: none; }
  .summary-row b { font-size: 14px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }

  @media (max-width: 900px) {
    .dash-header-inner, .dash-main { padding-left: 16px; padding-right: 16px; }
    .two-col { grid-template-columns: 1fr; }
  }
`;

// Canonical order mirrors pipelineService.STAGES, kept as a literal here
// (rather than fetched) since the board needs it before the first API
// response arrives, to render column headers immediately.
const STAGE_ORDER = [
  "new",
  "contacted",
  "qualified",
  "proposal",
  "won",
  "lost",
];
const STAGE_LABELS = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  proposal: "Proposal",
  won: "Won",
  lost: "Lost",
};
const STAGE_HINTS = {
  new: "Opportunity identified, but no outreach has been sent yet.",
  contacted: "Outreach has been sent and you're waiting on a reply.",
  qualified:
    "The opportunity has shown genuine interest and is worth pursuing.",
  proposal: "A proposal or quote has been sent and is being considered.",
  won: "The deal closed successfully.",
  lost: "The opportunity didn't convert. Lost deals leave the active board.",
};
const TERMINAL_STAGES = new Set(["won", "lost"]);

// Mirrors pipelineService.isValidTransition: one step forward/back, or
// straight to "lost" from any non-terminal stage. Kept in sync with the
// server rule so a drag target that would be rejected never even accepts
// the drop (rather than accepting it and then surfacing a 400).
function isValidTransition(fromStage, toStage) {
  if (fromStage === toStage) return false;
  if (TERMINAL_STAGES.has(fromStage)) return false;
  if (toStage === "lost") return true;
  const diff = STAGE_ORDER.indexOf(toStage) - STAGE_ORDER.indexOf(fromStage);
  return diff === 1 || diff === -1;
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export default function PipelineBoard({
  backTo = "/super-admin",
  embedded = false,
  darkMode: hostDarkMode,
}) {
  const { logout } = useAuth();
  const api = useRevenueApi();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [draggedCard, setDraggedCard] = useState(null); // { id, stage } of card being dragged
  const [dragOverStage, setDragOverStage] = useState(null); // stage the pointer is currently over
  const [ownDarkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  // Embedded in CustomerDashboard: the host owns the theme.
  const followsHost = embedded && hostDarkMode !== undefined;
  const darkMode = followsHost ? hostDarkMode : ownDarkMode;

  useEffect(() => {
    if (followsHost) return;
    localStorage.setItem("axo_theme", ownDarkMode ? "dark" : "light");
  }, [ownDarkMode, followsHost]);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPipelineBoard();
      setBoard(res.data);
    } catch (err) {
      setError("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  // Moves a card between two stage buckets in local board state without
  // waiting on the server, so a drop (or a Back/Forward/Lost click) is
  // reflected on the board immediately instead of only after loadBoard()'s
  // round-trip resolves. Reused to both apply the optimistic move and to
  // roll it back if the server rejects it.
  const moveCardInBoard = (
    prevBoard,
    customerCompanyId,
    fromStage,
    toStage,
  ) => {
    if (!prevBoard?.byStage) return prevBoard;
    const fromList = prevBoard.byStage[fromStage] || [];
    const card = fromList.find(
      (c) => c.customerCompanyId === customerCompanyId,
    );
    if (!card) return prevBoard;
    const toList = prevBoard.byStage[toStage] || [];
    return {
      ...prevBoard,
      byStage: {
        ...prevBoard.byStage,
        [fromStage]: fromList.filter(
          (c) => c.customerCompanyId !== customerCompanyId,
        ),
        [toStage]: [...toList, { ...card, stage: toStage }],
      },
    };
  };

  // Shared by both the Back/Forward/Lost buttons and drag-and-drop, same
  // £ prompt for "won", same error surfacing, same in-flight guard, so a
  // drag can never do something the buttons wouldn't also allow (the
  // server still re-validates via pipelineService.isValidTransition
  // either way, this is just so drags fail with the same message).
  const moveCard = async (customerCompanyId, fromStage, toStage) => {
    let dealValueGbp;
    if (toStage === "won") {
      const raw = window.prompt(
        "Deal value (£), required to mark this opportunity as won:",
      );
      if (raw === null) return; // user cancelled
      const value = Number(raw);
      if (!raw.trim() || Number.isNaN(value) || value <= 0) {
        setError("Enter a positive deal value to mark an opportunity as won.");
        return;
      }
      dealValueGbp = value;
    }

    setMovingId(customerCompanyId);
    setError(null);
    // Optimistic move: card shows in its new column right away.
    setBoard((prev) =>
      moveCardInBoard(prev, customerCompanyId, fromStage, toStage),
    );
    try {
      await api.movePipelineStage(customerCompanyId, toStage, dealValueGbp);
      await loadBoard();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to move stage");
      // Server rejected it, put the card back where it was.
      setBoard((prev) =>
        moveCardInBoard(prev, customerCompanyId, toStage, fromStage),
      );
    } finally {
      setMovingId(null);
    }
  };

  const handleMove = (customerCompanyId, fromStage, toStage) =>
    moveCard(customerCompanyId, fromStage, toStage);

  const handleDragStart = (e, card) => {
    setDraggedCard({ id: card.customerCompanyId, stage: card.stage });
    // Needed for Firefox, which requires data to be set for a drag to start.
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", card.customerCompanyId);
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDragOverStage(null);
  };

  const handleColumnDragOver = (e, stage) => {
    if (!draggedCard || !isValidTransition(draggedCard.stage, stage)) return;
    e.preventDefault(); // required to allow a drop
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const handleColumnDragLeave = (stage) => {
    setDragOverStage((current) => (current === stage ? null : current));
  };

  const handleColumnDrop = async (e, stage) => {
    e.preventDefault();
    const dragged = draggedCard;
    setDraggedCard(null);
    setDragOverStage(null);
    if (!dragged || !isValidTransition(dragged.stage, stage) || movingId)
      return;
    // handleColumnDragOver already keeps invalid targets from accepting a
    // drop, but the server (pipelineService.isValidTransition) stays the
    // single source of truth, moveCard's catch still surfaces a rejection
    // as the usual board-error banner and rolls the optimistic move back.
    await moveCard(dragged.id, dragged.stage, stage);
  };

  const subnavSlot = useSubnavSlot();

  const counts = board?.counts || {};
  const openStages = ["new", "contacted", "qualified", "proposal"];
  const openCount = openStages.reduce(
    (sum, stage) => sum + (counts[stage] || 0),
    0,
  );
  const wonCount = counts.won || 0;
  const lostCount = counts.lost || 0;
  const closedCount = wonCount + lostCount;
  const winRate = closedCount > 0 ? wonCount / closedCount : null;
  const totalTracked = board?.totalCount ?? 0;

  const rootInner = (
    <div className={`dash-root${darkMode ? " dark" : ""}`}>
      <style>
        {embedded
          ? scopeCss(styles, EMBED_SCOPE) +
            `${EMBED_SCOPE} .dash-root { min-height: 0; background: var(--bg); color: var(--text); }
             ${EMBED_SCOPE} .dash-main { max-width: none; padding: 0; }`
          : styles}
      </style>

      {embedded ? (
        subnavSlot &&
        createPortal(
          <button
            className="btn btn-ghost btn-sm"
            onClick={loadBoard}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>,
          subnavSlot,
        )
      ) : (
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <Link to={backTo} className="btn-back" title="Back to Dashboard">
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </Link>
              <div className="dash-brand-text">
                <div className="dash-brand-name">Pipeline</div>
                <div className="dash-brand-sub">AXO Revenue Intelligence</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                className="btn-header"
                onClick={loadBoard}
                disabled={loading}
              >
                {loading ? "Loading..." : "Refresh"}
              </button>
              <ThemeSwitch
                iconOnly
                theme={darkMode ? "dark" : "light"}
                onThemeChange={(next, variant, trigger) =>
                  applyThemeWithTransition(next, trigger, setDarkMode, variant)
                }
              />
              <button onClick={logout} className="btn-header">
                Logout
              </button>
            </div>
          </div>
        </header>
      )}

      <main className="dash-main">
        {error && <div className="board-error">{error}</div>}

        {loading && !board ? (
          <div style={{ width: "100%", padding: "8px 0" }}>
            <Skeleton lines={6} label="Loading pipeline" />
          </div>
        ) : (
          <>
            <div className="metric-grid">
              <div className="metric-card">
                <div className="metric-label">Open opportunities</div>
                <div className="metric-value">{openCount}</div>
                <div className="metric-note">New → Proposal</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">In proposal</div>
                <div className="metric-value">{counts.proposal || 0}</div>
                <div className="metric-note">Late-stage opportunities</div>
              </div>
              <div className="metric-card is-hero">
                <div className="metric-label">Won</div>
                <div className="metric-value">{wonCount}</div>
                <div className="metric-note">Closed successfully</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Win rate</div>
                <div className="metric-value">
                  {winRate != null ? `${Math.round(winRate * 100)}%` : "—"}
                </div>
                <div className="metric-note">Won ÷ closed</div>
              </div>
            </div>

            <div className="chart-card board-card">
              <div className="board-scroll">
                <div className="section-head">
                  <div className="section-title">
                    Pipeline stages
                    <InfoTooltip
                      label="How does the pipeline board work?"
                      content="Active pipeline: eligible leads with a pipeline stage. New is the default stage, so untouched leads appear in New. Each opportunity moves left to right as it progresses. Drag a card between columns, or use the Back / Forward / Lost buttons on the card, to change its stage."
                    />
                  </div>
                  <p className="section-caption">
                    Active pipeline · Eligible leads by stage · All time
                  </p>
                </div>
                <div className="board">
                  {STAGE_ORDER.map((stage) => {
                    const cards = board?.byStage?.[stage] || [];
                    const count = board?.counts?.[stage] ?? cards.length;
                    const stageIdx = STAGE_ORDER.indexOf(stage);
                    return (
                      <div className="board-col" key={stage}>
                        <div className="board-col-header">
                          <span className={`board-col-title ${stage}`}>
                            {STAGE_LABELS[stage]}
                            <InfoTooltip
                              label={`What does ${STAGE_LABELS[stage]} mean?`}
                              content={STAGE_HINTS[stage]}
                            />
                          </span>
                          <span className="board-col-count">{count}</span>
                        </div>
                        <div
                          className={`board-col-body${
                            dragOverStage === stage ? " drag-over" : ""
                          }`}
                          onDragOver={(e) => handleColumnDragOver(e, stage)}
                          onDragLeave={() => handleColumnDragLeave(stage)}
                          onDrop={(e) => handleColumnDrop(e, stage)}
                        >
                          {cards.length === 0 ? (
                            <div className="empty-col">No opportunities</div>
                          ) : (
                            cards.map((card) => {
                              const isTerminal = TERMINAL_STAGES.has(
                                card.stage,
                              );
                              const prevStage = STAGE_ORDER[stageIdx - 1];
                              const nextStage = STAGE_ORDER[stageIdx + 1];
                              const canGoBack =
                                !isTerminal && Boolean(prevStage);
                              const canGoForward =
                                !isTerminal && Boolean(nextStage);
                              const busy = movingId === card.customerCompanyId;
                              return (
                                <div
                                  className={`pipe-card${
                                    !isTerminal ? " draggable" : ""
                                  }${
                                    draggedCard?.id === card.customerCompanyId
                                      ? " dragging"
                                      : ""
                                  }`}
                                  key={card.customerCompanyId}
                                  draggable={!isTerminal && !busy}
                                  onDragStart={(e) => handleDragStart(e, card)}
                                  onDragEnd={handleDragEnd}
                                >
                                  <div className="pipe-card-name">
                                    {card.companyName}
                                  </div>
                                  <div className="pipe-card-sub">
                                    {card.customerName && (
                                      <span>{card.customerName}</span>
                                    )}
                                    <span className="pipe-card-product">
                                      {productLabel(card.product)}
                                    </span>
                                    {card.stageChangedAt && (
                                      <span>
                                        {formatDate(card.stageChangedAt)}
                                      </span>
                                    )}
                                  </div>
                                  {!isTerminal && (
                                    <div className="pipe-card-actions">
                                      <button
                                        className="btn-mini"
                                        disabled={!canGoBack || busy}
                                        onClick={() =>
                                          handleMove(
                                            card.customerCompanyId,
                                            card.stage,
                                            prevStage,
                                          )
                                        }
                                        title={
                                          canGoBack
                                            ? `Move back to ${STAGE_LABELS[prevStage]}`
                                            : "No previous stage"
                                        }
                                      >
                                        ← Back
                                      </button>
                                      <button
                                        className="btn-mini"
                                        disabled={!canGoForward || busy}
                                        onClick={() =>
                                          handleMove(
                                            card.customerCompanyId,
                                            card.stage,
                                            nextStage,
                                          )
                                        }
                                        title={
                                          canGoForward
                                            ? `Advance to ${STAGE_LABELS[nextStage]}`
                                            : "No next stage"
                                        }
                                      >
                                        Forward →
                                      </button>
                                      <button
                                        className="btn-mini lost"
                                        disabled={busy}
                                        onClick={() =>
                                          handleMove(
                                            card.customerCompanyId,
                                            card.stage,
                                            "lost",
                                          )
                                        }
                                        title="Mark lost"
                                      >
                                        Lost
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="two-col">
              <div>
                <div className="section-head">
                  <div className="section-title">Funnel health</div>
                  <p className="section-caption">
                    Open pipeline · Share of open opportunities
                  </p>
                </div>
                <div className="table-wrap">
                  {openStages.map((stage) => {
                    const count = counts[stage] || 0;
                    const share =
                      openCount > 0 ? Math.round((count / openCount) * 100) : 0;
                    return (
                      <div className="breakdown-row" key={stage}>
                        <div className="breakdown-main">
                          <div className="breakdown-task">
                            {STAGE_LABELS[stage]} · {count}
                          </div>
                          <div className="breakdown-bar-track">
                            <div
                              className="breakdown-bar-fill"
                              style={{
                                width: `${Math.max(share, count > 0 ? 3 : 0)}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="breakdown-cost">{share}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="section-head">
                  <div className="section-title">Pipeline summary</div>
                  <p className="section-caption">All time</p>
                </div>
                <div className="table-wrap">
                  {[
                    ["Open opportunities", openCount],
                    ["Won", wonCount],
                    ["Lost", lostCount],
                    [
                      "Win rate",
                      winRate != null ? `${Math.round(winRate * 100)}%` : "—",
                    ],
                    ["Total tracked", totalTracked],
                  ].map(([label, value]) => (
                    <div className="summary-row" key={label}>
                      <span>{label}</span>
                      <b>{value}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );

  return embedded ? (
    <div className="ri-embed-pipeline">{rootInner}</div>
  ) : (
    rootInner
  );
}
